import Vue from 'vue';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { buildDocumentMetadata, transformDataIntoFields, pathPartsToFlatKey } from '../features/docmaster/components/utils';

// Shown to the advisor, so say what happened rather than surfacing a status code.
const describeFetchFailure = (docId, res) => {
  if (res.status === 403) return 'You do not have permission to view this document.';
  if (res.status === 404) return 'This document no longer exists.';
  return `Failed to fetch docupload ${docId}: ${res.status} ${res.statusText}`;
};

export const useInspectorStore = defineStore('inspector', () => {
  const currentDocId = ref(null); // external_id
  const pdfDoc = ref(null);
  const pdfPage = ref(null);
  const totalPages = ref(0);
  const currentPage = ref(1);
  const lastPdfPage = ref(0);
  const pdfScale = ref(1);
  const pdfViewer = ref(null);
  const pdfCanvas = ref(null);
  const cursorBadge = ref(null);
  const highlightCanvas = ref(null);
  const nx = ref(null);
  const ny = ref(null);
  const cachedViewport = ref(null);
  const fileType = ref('pdf'); // 'pdf' | 'image'
  const imageElement = ref(null);
  const imageDimensions = ref(null);

  const documents = ref({});
  const loading = ref({});
  const error = ref({});

  const collapsedSet = ref(new Set());
  const ignoredFields = ref(new Set());
  const hoveredZones = ref(new Set()); // Hovered on the canvas
  const clickedZones = ref(new Set());
  const selectedZones = ref(new Set()); // Hovered in the fields list
  const fields = ref([]);
  const documentMetadata = ref({ documentTypes: [] });
  const additionalPage = ref(true); // Needs to be true if some fields have no page, metadata, or bbox

  // get data
  const setDataFromExtractInfo = extractInfo => {
    // Defensive: allow different shapes (array or object with known keys)
    // fields.value = transformDataIntoFields(extractInfo);
    fields.value = transformDataIntoFields(extractInfo).map(f => ({
      ...f,
      originalValue: f.originalValue ?? f.value ?? null,
    }));
    documentMetadata.value = buildDocumentMetadata(extractInfo);

    // 3) For each field, compute the key we use to look up metadata
    fields.value.forEach(f => {
      const stringPath =
        f.stringPath ??
        (Array.isArray(f.path) ? pathPartsToFlatKey(f.path) : String(f.path));
    });

    // Determine whether you need the extra “virtual” page for metadata/no-bbox fields
    const isScalar = v =>
      v === null ||
      ['string', 'number', 'boolean'].includes(typeof v);
    const needsExtra = fields.value.some(f =>
      isScalar(f.value) && (!f.pages?.length || !f.metadata?.bbox)
    );
    additionalPage.value = !!needsExtra;

    // If we already have a PDF loaded, refresh highlights for page 1
    if (pdfDoc.value) {
      totalPages.value = (additionalPage.value ? pdfDoc.value.numPages + 1 : pdfDoc.value.numPages);
      currentPage.value = Math.min(Math.max(1, currentPage.value), totalPages.value);
      renderHighlightLayer();
    }
  };

  const resetInspectorState = () => {
    fields.value = [];
    documentMetadata.value = { documentTypes: [] };
    hoveredZones.value = new Set();
    clickedZones.value = new Set();
    selectedZones.value = new Set();
    ignoredFields.value = new Set();
    collapsedSet.value = new Set();
    currentPage.value = 1;
    cachedViewport.value = null;
  };


  const fetchDocupload = async (docId, { worker } = {}) => {
    if (!docId) return;
    currentDocId.value = docId;

    Vue.set(loading.value, docId, true);
    Vue.set(error.value, docId, null);

    try {
      // GET the docupload detail
      const res = await fetch(`/api/v1/docupload/${encodeURIComponent(docId)}/`, {
        method: 'GET',
        credentials: 'include', // keep cookies/session for Django auth
        headers: { 'Accept': 'application/json' },
      });

      if (!res.ok) {
        throw new Error(describeFetchFailure(docId, res));
      }

      const payload = await res.json();
      console.log('[fetchDocupload] docId param =', docId);
      console.log('[fetchDocupload] payload =', payload);

      const doc = payload.document;
      if (!doc) {
        console.error('[fetchDocupload] No payload.document!');
        Vue.set(error.value, docId, 'This document could not be loaded.');
        return;
      }
      console.log('[fetchDocupload] normalized doc external_id =', doc.external_id);
      Vue.set(documents.value, docId, payload.document);

      // Determine document type from backend response
      const filetype = doc.file_type || '';
      console.log('FILE TYPE HERE', filetype);
      const imagePreviewTypes = [
        // image file types that we CAN display in browser with <img> tag
        'png', 'image/png',
        'jpg', 'jpeg', 'image/jpeg',
        'gif', 'image/gif',
        'webp', 'image/webp',
        'apng', 'image/apng',
        'bmp', 'image/bmp', 
      ];
      
      const isViewableImage = imagePreviewTypes.some(type =>
        filetype.toLowerCase().includes(type)
      );      
      const isPdf = filetype.toLowerCase().includes('pdf');

      // reset + load PDF
      resetInspectorState();
      const pdfUrl = payload.document?.download_url || payload.pdf_url || payload.document_url;
      const imageUrl = payload.document?.preview_url || payload.preview_url;
      if (isPdf) {
        fileType.value = 'pdf';
        await loadPdf(pdfUrl, { worker });
      } else if (isViewableImage) {
        fileType.value = 'image';
        await loadImage(imageUrl);
      } else {
        fileType.value = filetype || 'other';
        // Handle other types or show error
        console.warn('[fetchDocupload] Unsupported file type for PREVIEW:', fileType);
      }
      console.log('FILE TYPE VAR HERE', fileType.value)

      // fields/metadata
      const extract = payload.extract_info || payload.document?.extract_info;
      if (extract) setDataFromExtractInfo(extract);

      // initial paint
      await renderPdfPage(currentPage.value);
      renderHighlightLayer();
    } catch (e) {
      // error is a ref, so it has to be written through .value for getError to see it
      Vue.set(error.value, docId, e.message || String(e));
    } finally {
      Vue.set(loading.value, docId, false);
    }
  };


  // Getters
  const getDocument = id => documents.value[id] ?? null;
  const isLoading = id => !!loading.value[id];
  const getError = id => error.value[id] ?? null;

  // PDF Rendering
  const setDOMElements = (viewerEl, canvasEl, highlightEl, badgeEl) => {
    pdfViewer.value = viewerEl || null;
    pdfCanvas.value = canvasEl || null; // null for images
    cursorBadge.value = badgeEl || null;
    highlightCanvas.value = highlightEl || null;

    // Detach from old element first
    // if (highlightCanvas.value) detachHighlightListeners();

    highlightCanvas.value = highlightEl || null;

    // Attach to new element
    // if (highlightCanvas.value) attachHighlightListeners();
  };

  const setImageDimensions = (width, height) => {
    imageDimensions.value = { width, height };
    
    if (highlightCanvas.value) {
      highlightCanvas.value.width = width;
      highlightCanvas.value.height = height;
      highlightCanvas.value.style.aspectRatio = `${width} / ${height}`;
    }
    // Automatically render highlights after setting dimensions
    renderHighlightLayer();
  };

  const drawHighlight = (path, bbox, viewport, ctx, color = '#7957d5') => {
    // bbox is normalized: { top, left, width, height } in [0,1]
    const W = viewport.width;
    const H = viewport.height;

    const x = (bbox.left || bbox.x) * W; // bbox.x from old docuploads
    const y = (bbox.top || bbox.y) * H; //bbox.y from old docuploads

    const w = bbox.width * W;
    const h = bbox.height * H;

    ctx.save();
    ctx.lineWidth = 1;

    ctx.strokeStyle = color;
    ctx.fillStyle = `${color}66`;
    ctx.fillRect(x, y, w, h);
    ctx.strokeRect(x, y, w, h);
    ctx.restore();
  };

  const bboxContains = (bbox, nx, ny, { padding = 0 } = {}) => {
    const x0 = (bbox.left || bbox.x) - padding;
    const y0 = (bbox.top || bbox.y) - padding;
    const x1 = (bbox.left || bbox.x) + bbox.width + padding;
    const y1 = (bbox.top || bbox.y) + bbox.height + padding;

    return nx >= x0 && nx <= x1 && ny >= y0 && ny <= y1;
  };

  // Async
  const loadPdf = async (url, { worker } = {}) => {
    if (!url) return;
    fileType.value = 'pdf';
    pdfDoc.value = await pdfjsLib.getDocument({
      url,
      isEvalSupported: false,
      ...(worker ? { worker } : {}),
    }).promise;

    lastPdfPage.value = pdfDoc.value.numPages;
    totalPages.value = additionalPage.value ? pdfDoc.value.numPages + 1 : pdfDoc.value.numPages;

    // keep current page in range
    currentPage.value = Math.min(
        Math.max(1, currentPage.value),
        totalPages.value,
    );
    await renderPdfPage(currentPage.value);
    renderHighlightLayer();
  };

  const handleCanvasClick = (nx, ny) => {
    const hits = fieldZonesHit(nx, ny);
    clickedZones.value = hits;
    renderHighlightLayer();
  };

  const currentRenderTask = ref(null);
  const isRendering = ref(false);
  const pendingPageNumber = ref(null);

  const renderPdfPage = async pageNumber => {
    if (!pdfDoc.value || !pdfCanvas.value || !highlightCanvas.value) return;

    if (isRendering.value) {
      pendingPageNumber.value = pageNumber;
      return;
    }

    isRendering.value = true;

    try {
      // If a render is already in progress, cancel it first
      if (currentRenderTask.value) {
        try {
          currentRenderTask.value.cancel();
          await currentRenderTask.value.promise.catch(() => {});
        } catch (e) {
          console.error(e);
        }
        currentRenderTask.value = null;
      }

      const page = await pdfDoc.value.getPage(pageNumber);
      pdfPage.value = page;

      const pdfCtx = pdfCanvas.value.getContext('2d');
      const viewport = page.getViewport({ scale: pdfScale.value });
      cachedViewport.value = viewport;

      const dpr = window.devicePixelRatio || 1;
      pdfCanvas.value.width = Math.floor(viewport.width * dpr);
      pdfCanvas.value.height = Math.floor(viewport.height * dpr);
      pdfCanvas.value.style.aspectRatio = `${viewport.width} / ${viewport.height}`;
      pdfCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

      highlightCanvas.value.width = Math.floor(viewport.width);
      highlightCanvas.value.height = Math.floor(viewport.height);
      highlightCanvas.value.style.aspectRatio = `${viewport.width} / ${viewport.height}`;

      // render pdf page
      const task = page.render({ canvasContext: pdfCtx, viewport });
      currentRenderTask.value = task;

      await task.promise;
    } catch (e) {
      console.error('Error rendering PDF page (printed from docinspectorstore):', e);
    } finally {
      currentRenderTask.value = null;
      isRendering.value = false;

      // If another page was requested while rendering, render it next
      if (pendingPageNumber.value != null) {
        const next = pendingPageNumber.value;
        pendingPageNumber.value = null;
        renderPdfPage(next);
      }
    }
  };

  const renderHighlightLayer = () => {
    if (!highlightCanvas.value) return;
    console.log('[renderHighlightLayer] fileType:', fileType.value);
    console.log('[renderHighlightLayer] currentPage:', currentPage.value);
    console.log('[renderHighlightLayer] fields:', fields.value.length);
    console.log('[renderHighlightLayer] selectedZones:', selectedZones.value);
    // Determine viewport based on document type
    let viewport;
    if (fileType.value === 'pdf') {
      if (!cachedViewport.value) return;
      viewport = cachedViewport.value;
    } else if (fileType.value === 'image') {
      if (!imageDimensions.value) return;
      viewport = {
        width: imageDimensions.value.width,
        height: imageDimensions.value.height,
      };
    } else {
      return;
    }

    const highlightCtx = highlightCanvas.value.getContext('2d');
    const hits = new Set();

    // clear highlight
    highlightCtx.clearRect(
        0,
        0,
        highlightCanvas.value.width,
        highlightCanvas.value.height,
    );

    fields.value.forEach(field => {
      const stringPath = getStringPath(field);
      const pageToCheck = fileType.value != 'pdf' ? 1 : currentPage.value; // For images, page is always 1
      // For PDFs, check current page. For images/unsupported, show all fields
      const shouldShowField = fileType.value === 'pdf' 
      ? field.pages.includes(currentPage.value)
      : true; // Show all fields for non-PDF types

      if (
        field.metadata?.bbox &&
        (field.pages.includes(pageToCheck) || shouldShowField) &&
        selectedZones.value.has(stringPath)
      ) {
        drawHighlight(
            stringPath,
            field.metadata.bbox,
            viewport,
            highlightCtx,
        );

        if (bboxContains(field.metadata.bbox, nx.value, ny.value)) {
          hits.add(stringPath);
        }
      }
    });

    hoveredZones.value = hits;
    if (highlightCanvas.value) {
      highlightCanvas.value.style.cursor = hits.size > 0 ? 'pointer' : 'default';
    }
  };

  const loadImage = async (url) => {
    if (!url) return;
    
    fileType.value = 'image';
    totalPages.value = 1;
    currentPage.value = 1;
    
    // Image will call setImageDimensions when it loads
    // Then renderHighlightLayer will be called
  };
  

  // Events
  const hoverEnter = stringPath => {
    const next = new Set(selectedZones.value);
    next.add(stringPath);
    selectedZones.value = next;
  };

  const hoverLeave = stringPath => {
    const next = new Set(selectedZones.value);
    next.delete(stringPath);
    selectedZones.value = next;
  };

  const toggleCollapse = stringPath => {
    if (collapsedSet.value.has(stringPath)) {
      collapsedSet.value.delete(stringPath);
    } else {
      collapsedSet.value.add(stringPath);
    }

    // ensure Vue reactivity (since Set mutations aren't tracked deeply)
    collapsedSet.value = new Set(collapsedSet.value);
  };

  const toggleIgnore = rootPath => {
    const next = new Set(ignoredFields.value);
    const isAdding = !next.has(rootPath);

    // Build a map from stringPath -> field for quick lookup
    const pathToField = new Map();
    fields.value.forEach(f => {
      const p = getStringPath(f);
      if (typeof p === 'string' && p.length > 0) {
        pathToField.set(p, f);
      }
    });
    if (isAdding) {
      // add root + descendants
      pathToField.forEach((field, path) => {
        if (!field.pages.includes(currentPage.value)) return;

        if (path === rootPath || path.startsWith(rootPath + '.')) {
          next.add(path);
        }
      });
    } else {
      // remove root + descendants
      next.forEach(p => {
        const field = pathToField.get(p);
        if (!field) return;
        if (!field.pages.includes(currentPage.value)) return;
  
        if (p === rootPath || p.startsWith(rootPath + '.')) {
          next.delete(p);
        }
      });
    }

    ignoredFields.value = next;
  };

  const setFieldValue = (stringPath, value) => {
    // Find index by stringPath, or fall back to joining path
    const i = fields.value.findIndex(f => getStringPath(f) === stringPath);
    // const i = fields.value.findIndex(f => {
    //   const key =
    //     f.stringPath ??
    //     (Array.isArray(f.path) ? f.path.map(String).join('.') : String(f.path));
    //   return key === stringPath;
    // });

    if (i === -1) {
      console.error(`No target found at ${stringPath}`);
      return;
    }

    const prev = fields.value[i];
    // Use splice to ensure reactivity in Vue 2/Pinia
    fields.value.splice(i, 1, { ...prev, value });
  };

  // const getStringPath = f =>
  //   f.stringPath ??
  //   (Array.isArray(f.path) ? pathPartsToFlatKey(f.path) : String(f.path));
  
  const getStringPath = f => {
    if (!f) return null;
  
    if (typeof f.stringPath === 'string' && f.stringPath.length > 0) {
      return f.stringPath;
    }
  
    if (Array.isArray(f.path)) {
      return pathPartsToFlatKey(f.path);
    }
    if (f.path != null) {
      return String(f.path);
    }
    return null;
  };

  const setCurrentCursorPosition = (x, y) => {
    nx.value = x;
    ny.value = y;
    renderHighlightLayer();
  };
  const submitForm = () => {
    if (!currentDocId.value) {
      console.error('submitForm called with no currentDocId');
      return null;
    }
  
    // 1) Precompute paths
    const allPaths = fields.value
      .map(getStringPath)
      .filter(p => typeof p === 'string' && p.length > 0);
  
    const isLeafPath = path =>
      typeof path === 'string' &&
      !allPaths.some(
        other =>
          other !== path &&
          typeof other === 'string' &&
          other.startsWith(path + '.'),
      );
  
    // 2) Deletions: everything in ignoredFields, but only leaf nodes
    const deletions = [];
    ignoredFields.value.forEach(ignoredPath => {
      if (typeof ignoredPath !== 'string') return;
  
      const field = fields.value.find(f => getStringPath(f) === ignoredPath);
      if (!field) return;
  
      const path = getStringPath(field);
      if (!isLeafPath(path)) {
        // don't send deletions for container/group nodes
        return;
      }
  
      deletions.push({
        segment: field.segment || field.segmentKey || null,
        path: field.path,
        stringPath: path,
        pages: field.pages || [],
        oldValue: field.originalValue ?? null,
      });
    });
  
    // 3) Updates: non-ignored *leaf* fields where value changed
    const updates = fields.value
      .map(f => {
        const path = getStringPath(f);
        return { field: f, path };
      })
      .filter(({ path }) => typeof path === 'string' && path.length > 0)
      .filter(({ field, path }) => {
        return (
          !ignoredFields.value.has(path) &&
          isLeafPath(path) &&
          field.value !== field.originalValue
        );
      })
      .map(({ field, path }) => ({
        segment: field.segment || field.segmentKey || null,
        path: field.path,
        stringPath: path,
        pages: field.pages || [],
        oldValue: field.originalValue ?? null,
        newValue: field.value,
      }));
  
    const payload = {
      docupload_id: currentDocId.value,
      updates,
      deletions,
    };
  
    console.log('[InspectorStore] submit payload', payload);
  
    return payload;
  };
  
  

  // Navigation
  const nextPage = async () => {
    if (!pdfDoc.value) return;
    const next = Math.min(currentPage.value + 1, totalPages.value);
    if (next !== currentPage.value) {
      currentPage.value = next;

      // The last page might be out of range
      if (next <= lastPdfPage.value) {
        await renderPdfPage(next);
        renderHighlightLayer();
      }
    }
  };

  const prevPage = async () => {
    if (!pdfDoc.value) return;
    const prev = Math.max(currentPage.value - 1, 1);
    if (prev !== currentPage.value) {
      await renderPdfPage(prev);
      currentPage.value = prev;
      renderHighlightLayer();
    }
  };

  // Computed
  const currentDocumentType = computed(() => documentMetadata.value.documentTypes[currentPage.value - 1]);

  // Listeners
  const fieldZonesHit = (nxVal, nyVal) => {
    const hits = new Set();
    if (!fields.value) return hits;

    fields.value.forEach(f => {
      if (f?.metadata?.bbox && f?.pages?.includes(currentPage.value)) {
        if (bboxContains(f.metadata.bbox, nxVal, nyVal)) {
          hits.add(getStringPath(f) || f.path.join('.'));
        }
      }
    });

    return hits;
  };

  let onClick = null;
  let onMove = null;
  let onLeave = null;

  const attachHighlightListeners = () => {
    const el = highlightCanvas.value;
    if (!el) return;

    // cleanup any old handlers first
    if (onClick) el.removeEventListener('click', onClick);
    if (onMove) el.removeEventListener('pointermove', onMove);
    if (onLeave) el.removeEventListener('pointerleave', onLeave);

    onMove = e => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const nnx = x / rect.width;
      const nny = y / rect.height;

      setCurrentCursorPosition(nnx, nny); // this also calls renderHighlightLayer()

      // compute hits immediately so we can set cursor without waiting for redraw
      const hits = fieldZonesHit(nnx, nny);
      hoveredZones.value = hits;
      el.style.cursor = hits.size > 0 ? 'pointer' : 'default';
    };

    onLeave = () => {
      hoveredZones.value = new Set();
      el.style.cursor = 'default';
      // (optional) re-render to show de-emphasized state when leaving
      renderHighlightLayer();
    };

    onClick = e => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const nnx = x / rect.width;
      const nny = y / rect.height;

      const hits = fieldZonesHit(nnx, nny);

      clickedZones.value = hits;

      renderHighlightLayer();
    };

    el.addEventListener('pointermove', onMove, { passive: true });
    el.addEventListener('pointerleave', onLeave, { passive: true });
    el.addEventListener('click', onClick);
  };

  const detachHighlightListeners = () => {
    const el = highlightCanvas.value;
    if (!el) return;
    if (onClick) el.removeEventListener('click', onClick);
    if (onMove) el.removeEventListener('pointermove', onMove);
    if (onLeave) el.removeEventListener('pointerleave', onLeave);
    onClick = onMove = onLeave = null;
  };

  watch(selectedZones, () => {
    renderHighlightLayer();
  });

  return {
    currentDocId,
    documents,
    collapsedSet,
    ignoredFields,
    loading,
    error,
    fields,
    documentMetadata,
    currentDocumentType,
    currentPage,
    totalPages,
    additionalPage,
    nx,
    ny,
    hoveredZones,
    clickedZones,
    selectedZones,
    hoverEnter,
    hoverLeave,
    getDocument,
    setCurrentCursorPosition,
    isLoading,
    getError,
    toggleCollapse,
    toggleIgnore,
    setDOMElements,
    loadPdf,
    nextPage,
    prevPage,
    setFieldValue,
    submitForm,
    fetchDocupload,
    setDataFromExtractInfo,
    resetInspectorState,
    fileType,
    imageDimensions,
    setImageDimensions,
    loadImage,
    handleCanvasClick,
  };
});
